import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Plus, Edit, Trash2, Search, Loader2, Users, Eraser } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [wipeTarget, setWipeTarget] = useState(null);
  const [wiping, setWiping] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      toast.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingCompany) {
        await axios.put(
          `${API}/admin/companies/${editingCompany.id}`,
          { name: companyName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Company updated");
      } else {
        await axios.post(
          `${API}/admin/companies`,
          { name: companyName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Company created");
      }
      setDialogOpen(false);
      setCompanyName("");
      setEditingCompany(null);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save company");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setCompanyName(company.name);
    setDialogOpen(true);
  };

  const handleDelete = async (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company?.user_count > 0) {
      if (!confirm(`This company has ${company.user_count} users. Deleting will remove their company association. Continue?`)) {
        return;
      }
    }

    try {
      await axios.delete(`${API}/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Company deleted");
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete company");
    }
  };

  const handleWipe = async () => {
    if (!wipeTarget) return;
    setWiping(true);
    try {
      const res = await axios.post(
        `${API}/admin/companies/${wipeTarget.id}/wipe-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { transactions_deleted = 0, accounts_deleted = 0 } = res.data || {};
      toast.success(
        `Wiped ${wipeTarget.name}: ${transactions_deleted} transactions, ${accounts_deleted} accounts removed`
      );
      setWipeTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to wipe company data");
    } finally {
      setWiping(false);
    }
  };

  const filteredCompanies = companies.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="company-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Company Management
          </h1>
          <p className="text-[#5c6b63] mt-2">
            Manage organizations on the platform
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { 
          setDialogOpen(open); 
          if (!open) { setCompanyName(""); setEditingCompany(null); }
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary flex items-center gap-2" data-testid="add-company-btn">
              <Plus className="w-4 h-4" />
              Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingCompany ? "Edit Company" : "Add New Company"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation"
                  className="h-11"
                  data-testid="company-name-input"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full btn-accent h-11"
                data-testid="save-company-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingCompany ? "Update Company" : "Create Company"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="kpi-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5c6b63]">Total Companies</p>
                <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">{companies.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5c6b63]">Active Companies</p>
                <p className="text-2xl font-bold text-emerald-600 font-['Outfit']">
                  {companies.filter(c => c.is_active !== false).length}
                </p>
              </div>
              <Building2 className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5c6b63]">Total Users</p>
                <p className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">
                  {companies.reduce((acc, c) => acc + (c.user_count || 0), 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5c6b63]" />
            <Input
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-companies"
            />
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
            All Companies ({filteredCompanies.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1a2e22] animate-spin" />
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-[#c8d4cc] mx-auto mb-4" />
              <p className="text-[#5c6b63]">No companies found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="companies-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="font-medium">{company.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{company.user_count || 0} users</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={company.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                          {company.is_active !== false ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#5c6b63]">
                        {company.created_at ? new Date(company.created_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(company)}
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-amber-600"
                            title="Wipe transactions & chart of accounts"
                            onClick={() => setWipeTarget(company)}
                            data-testid={`wipe-company-${company.id}`}
                          >
                            <Eraser className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={() => handleDelete(company.id)}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wipe confirmation dialog */}
      <AlertDialog open={!!wipeTarget} onOpenChange={(open) => !open && setWipeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-['Outfit']">
              Wipe data for {wipeTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold text-[#1a2e22]">all transactions</span>{" "}
              and <span className="font-semibold text-[#1a2e22]">all Chart of Accounts entries</span> for{" "}
              <span className="font-semibold">{wipeTarget?.name}</span>.
              <br /><br />
              Users, suppliers, reports and CSRD checklist for this company will be kept untouched. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={wiping} data-testid="wipe-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleWipe(); }}
              disabled={wiping}
              className="bg-red-600 hover:bg-red-700"
              data-testid="wipe-confirm-btn"
            >
              {wiping ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, wipe data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
